import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, Github } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { projects } from '@/data/projects'
import { useSeo } from '@/hooks/useSeo'
import { trackEvent } from '@/lib/analytics'

const categories = [
  { id: 'all', label: 'All' },
  { id: 'ml', label: 'ML' },
  { id: 'optimization', label: 'Optimization' },
  { id: 'research', label: 'Research' },
  { id: 'infra', label: 'Infra' },
] as const

type CategoryId = (typeof categories)[number]['id']

export function ProjectsPage() {
  useSeo(
    'Projects',
    'Personal projects in optimization, machine learning, and infrastructure, with write-ups and live demos.'
  )
  const [active, setActive] = useState<CategoryId>('all')
  const filtered =
    active === 'all'
      ? projects
      : projects.filter((p) => p.category === active)

  return (
    <div className="container max-w-3xl pb-20 pt-12">
      <header className="mb-8 border-b border-border/40 pb-8">
        <h1 className="text-3xl font-semibold tracking-tight">Projects</h1>
        <p className="mt-2 text-muted-foreground">
          Some of what I've built in my own time, chasing an idea or
          tackling a real problem. Ranges from ML to optimization to
          regular SWE work. Feel free to take a look around.
        </p>
      </header>

      <div className="mb-8 flex flex-wrap gap-1">
        {categories.map((c) => (
          <Button
            key={c.id}
            variant={active === c.id ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setActive(c.id)}
            className="h-8"
          >
            {c.label}
          </Button>
        ))}
      </div>

      <ul className="divide-y divide-border/60">
        {filtered.map((project) => (
          <li key={project.id} className="group py-6 first:pt-0">
            <Link to={`/projects/${project.id}`} className="block">
              <div className="flex items-baseline justify-between gap-4">
                <h2 className="flex items-center gap-1.5 text-lg font-medium transition-colors group-hover:text-primary">
                  {project.title}
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground" />
                </h2>
              </div>
              <p className="mt-1.5 text-sm text-muted-foreground">
                {project.description}
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {project.technologies.slice(0, 6).map((tech) => (
                  <Badge
                    key={tech}
                    variant="secondary"
                    className="text-[11px] font-normal"
                  >
                    {tech}
                  </Badge>
                ))}
              </div>
            </Link>
            {project.githubUrl && (
              <div className="mt-3 flex flex-wrap gap-3 text-xs">
                <a
                  href={project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
                  onClick={() =>
                    trackEvent('project_link_click', { project_id: project.id, link_type: 'github' })
                  }
                >
                  <Github className="h-3 w-3" /> Source
                </a>
              </div>
            )}
          </li>
        ))}
      </ul>

      {filtered.length === 0 && (
        <p className="py-12 text-center text-sm text-muted-foreground">
          No projects in this category.
        </p>
      )}
    </div>
  )
}
